/**
 * /constitution/edit — propose a new constitution rule.
 *
 * Founder-only. The form submits a POST to /api/constitution/proposals
 * which starts a Temporal FounderApprovalGate workflow on the upstream.
 * After ack the user is redirected to /constitution/proposals with a
 * "Proposal submitted, awaiting approval workflow" toast.
 *
 * Severity selection has explicit gating: marking a rule "critical" makes
 * it immutable, which is a stronger commitment — the form requires a
 * change_summary in that case to anchor the rationale.
 */
import type { ConstitutionRuleProposal, ConstitutionSeverity } from '@chaos/types/constitution';
import { constitutionRuleProposalSchema } from '@chaos/types/constitution';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { ChevronLeft, Loader2, Scale } from 'lucide-react';
import type * as React from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { useToaster } from '../../../components/ui/toast';
import { constitutionApi, constitutionKeys } from '../../../lib/api/constitution';
import { cn } from '../../../lib/utils';
import { useAuthStore } from '../../../stores/auth-store';

const SEVERITIES: ConstitutionSeverity[] = ['high', 'medium', 'low', 'informational', 'critical'];
const SEV_LABEL: Record<ConstitutionSeverity, string> = {
  critical: 'Critical (Immutable)',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  informational: 'Informational',
};

function ProposalForm() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToaster();
  const form = useForm<ConstitutionRuleProposal>({
    resolver: zodResolver(constitutionRuleProposalSchema),
    defaultValues: {
      ruleKey: '',
      statement: '',
      rationale: '',
      severity: 'high',
      scope: 'global',
    },
  });

  const severity = form.watch('severity');
  const isCritical = severity === 'critical';

  const submit = useMutation({
    mutationFn: (input: ConstitutionRuleProposal) => constitutionApi.submitProposal(input),
    onSuccess: (ack) => {
      void qc.invalidateQueries({ queryKey: constitutionKeys.all });
      toast({
        title: 'Proposal submitted, awaiting approval workflow',
        description:
          ack.workflowId != null
            ? `Workflow ${ack.workflowId.slice(0, 12)}…`
            : 'Queued — Temporal worker will pick it up shortly.',
      });
      void navigate({ to: '/constitution/proposals' });
    },
    onError: (err: Error) => {
      toast({
        title: 'Proposal failed',
        description: err.message,
        variant: 'destructive',
      });
    },
  });

  const onSubmit = form.handleSubmit((values) => {
    submit.mutate(values);
  });

  return (
    <section className="mx-auto flex w-full max-w-3xl flex-col gap-6 p-6">
      <header className="flex items-baseline gap-3">
        <Scale className="h-6 w-6 text-foreground" aria-hidden />
        <div className="flex flex-col">
          <h1 className="text-[18px] font-semibold tracking-tight text-foreground">
            Propose constitution rule
          </h1>
          <p className="text-[12px] text-muted-foreground">
            Submissions enter the founder approval workflow before they take effect.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void navigate({ to: '/constitution' })}
          className="ml-auto inline-flex items-center gap-1 text-[12px] text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-3.5 w-3.5" aria-hidden /> Back
        </button>
      </header>

      <form className="flex flex-col gap-4" onSubmit={onSubmit}>
        <Field label="Rule key" hint="Snake-case identifier; unique across the constitution.">
          <Input
            placeholder="no_external_data_in_logs"
            {...form.register('ruleKey')}
            disabled={submit.isPending}
            aria-invalid={Boolean(form.formState.errors.ruleKey)}
          />
          <FieldError error={form.formState.errors.ruleKey?.message} />
        </Field>

        <Field label="Statement" hint="Plain-language directive every agent obeys.">
          <Textarea
            placeholder="Agents must never log raw API responses containing PII."
            rows={3}
            {...form.register('statement')}
            disabled={submit.isPending}
            aria-invalid={Boolean(form.formState.errors.statement)}
          />
          <FieldError error={form.formState.errors.statement?.message} />
        </Field>

        <Field label="Rationale" hint="Why does this exist? (optional but recommended)">
          <Textarea
            placeholder="PII leakage = breach. Logging redaction is a defense in depth."
            rows={2}
            {...form.register('rationale')}
            disabled={submit.isPending}
          />
        </Field>

        <Field label="Severity">
          <div className="flex flex-wrap items-center gap-1">
            {SEVERITIES.map((sev) => (
              <button
                key={sev}
                type="button"
                onClick={() => form.setValue('severity', sev, { shouldDirty: true })}
                aria-pressed={severity === sev}
                className={cn(
                  'rounded border px-2 py-1 text-[11px] transition-colors',
                  severity === sev
                    ? 'border-foreground/40 bg-foreground/5 text-foreground'
                    : 'border-border text-muted-foreground hover:bg-foreground/5 hover:text-foreground',
                )}
              >
                {SEV_LABEL[sev]}
              </button>
            ))}
          </div>
          {isCritical ? (
            <p className="text-[11px] text-destructive-foreground">
              Critical = immutable. Founder approval required, no shortcut.
            </p>
          ) : null}
        </Field>

        <Field
          label="Change summary"
          hint={
            isCritical
              ? 'Required when proposing a critical rule — anchors the audit trail.'
              : 'Short description of the change (audit log + workflow notification body).'
          }
        >
          <Input
            placeholder="Add PII guard rule per Q3 security review"
            {...form.register('changeSummary')}
            disabled={submit.isPending}
            required={isCritical}
          />
          <FieldError error={form.formState.errors.changeSummary?.message} />
        </Field>

        <div className="flex items-center gap-2">
          <Button type="submit" disabled={submit.isPending}>
            {submit.isPending ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />
                Submitting…
              </>
            ) : (
              'Submit proposal'
            )}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => void navigate({ to: '/constitution' })}
            disabled={submit.isPending}
          >
            Cancel
          </Button>
        </div>
      </form>
    </section>
  );
}

interface FieldProps extends React.PropsWithChildren {
  label: string;
  hint?: string;
}

function Field({ label, hint, children }: FieldProps) {
  return (
    <fieldset className="flex flex-col gap-1.5 border-0 p-0 text-[12px]">
      <legend className="mb-0 px-0 font-medium text-foreground">{label}</legend>
      {hint ? <span className="text-[11px] text-muted-foreground">{hint}</span> : null}
      {children}
    </fieldset>
  );
}

function FieldError({ error }: { error: string | undefined }) {
  if (!error) return null;
  return <p className="text-[11px] text-destructive-foreground">{error}</p>;
}

export const Route = createFileRoute('/_authenticated/constitution/edit')({
  beforeLoad: () => {
    const state = useAuthStore.getState();
    const ws = state.workspaces.find((w) => w.workspace.id === state.activeWorkspaceId);
    if (ws?.membership.role !== 'founder') {
      throw redirect({ to: '/constitution' });
    }
  },
  component: ProposalForm,
});
